import { Group } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { Icon, Text } from '@repo/foundations';
import { DataTable } from '@repo/mantine-datatable';
import { capitalize } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import type { IDatabuilderJobStage } from '../../../api';
import { TableV2Loader } from '../../TableV2';
import {
	FOOTER_HEIGHT,
	ROW_HEIGHT,
	useTableStyles,
} from '../../TableV2/TableV2.styles';
import {
	EXTRACTION_STATUS,
	getExtractionBadgeStyles,
	getExtractionStageDescription,
	getExtractionStageStatus,
} from './utils';

const RECORDS_PER_PAGE = 10;

interface ExtractionRowProps {
	stage: IDatabuilderJobStage;
	isJobTerminated: boolean;
}

function TimingRenderer({ stage, isJobTerminated }: ExtractionRowProps) {
	const status = getExtractionStageStatus(stage, isJobTerminated);
	const [desc, setDesc] = useState(
		getExtractionStageDescription(stage, isJobTerminated)
	);

	const { start, stop } = useInterval(() => {
		setDesc(getExtractionStageDescription(stage, isJobTerminated));
	}, 500);

	useEffect(() => {
		if (status !== EXTRACTION_STATUS.running) {
			setDesc(getExtractionStageDescription(stage, isJobTerminated));
			stop();
		} else {
			start();
		}
	}, [isJobTerminated, stage, start, status, stop]);

	return <Text size="xs">{desc}</Text>;
}

function StatusRenderer({ stage, isJobTerminated }: ExtractionRowProps) {
	const status = getExtractionStageStatus(stage, isJobTerminated);
	const extractionStyles = getExtractionBadgeStyles(status);
	return (
		<Group spacing="3xs" noWrap>
			<Icon
				size="md"
				name={extractionStyles.iconName}
				fillColor={extractionStyles.iconFillColor}
				color={extractionStyles.iconColor}
			/>
			<Text size="xs">{capitalize(status)}</Text>
		</Group>
	);
}

interface ExtractionStagesTableProps {
	isJobTerminated: boolean;
	stages: IDatabuilderJobStage[];
}

export function ExtractionStagesTable({
	isJobTerminated,
	stages,
}: ExtractionStagesTableProps) {
	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: true,
		noBorder: true,
		disableSelectedDisplay: true,
		withHover: true,
	});

	const columns = useMemo(
		() => [
			{
				title: 'Stage',
				accessor: 'stage',
				render: (record: IDatabuilderJobStage) => (
					<Text size="sm">{record.stage}</Text>
				),
			},
			{
				title: 'Timing',
				accessor: 'timing',
				render: (record: IDatabuilderJobStage) => (
					<TimingRenderer stage={record} isJobTerminated={isJobTerminated} />
				),
			},
			{
				title: 'Status',
				accessor: 'status',
				render: (record: IDatabuilderJobStage) => (
					<StatusRenderer stage={record} isJobTerminated={isJobTerminated} />
				),
			},
		],
		[isJobTerminated]
	);

	return (
		<DataTable<IDatabuilderJobStage>
			borderRadius="md"
			withBorder
			classNames={tableClasses}
			maxHeight={RECORDS_PER_PAGE * ROW_HEIGHT + FOOTER_HEIGHT}
			height={RECORDS_PER_PAGE * ROW_HEIGHT + FOOTER_HEIGHT}
			noRecordsIcon={<Icon size="lg" name="search" />}
			noRecordsText={'No stages found'}
			records={stages}
			columns={columns}
			customLoader={<TableV2Loader />}
		/>
	);
}
